import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { API } from '../App';
import NavBar from '../components/NavBar';

const STATUS_COLORS = {
  doing_well: { color: '#16a34a', bg: '#dcfce7', label: 'Stable', icon: '🟢' },
  slight_change: { color: '#ca8a04', bg: '#fef9c3', label: 'Watch', icon: '🟡' },
  significant_change: { color: '#ea580c', bg: '#ffedd5', label: 'Attention', icon: '🟠' },
  urgent: { color: '#dc2626', bg: '#fee2e2', label: 'Urgent', icon: '🔴' },
};

const Snap = ({ label, value, icon }) => (
  (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) ? null : (
    <div className="family-snap">
      <div className="family-snap-label"><i className={`fa-solid ${icon}`} /> {label}</div>
      <div className="family-snap-value">{Array.isArray(value) ? value.join(', ') : String(value)}</div>
    </div>
  )
);

const FamilyDashboard = ({ user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [logsByClient, setLogsByClient] = useState({});
  const [trendsByClient, setTrendsByClient] = useState({});
  const [entitlement, setEntitlement] = useState(null);
  const [prefs, setPrefs] = useState({ log_signed: true, fall: true, urgent: true, meds: true });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ent, cs, linked, prefsRes] = await Promise.all([
          axios.get(`${API}/user/entitlement`),
          axios.get(`${API}/clients`),
          axios.get(`${API}/family/linked-clients`).catch(() => ({ data: [] })),
          axios.get(`${API}/family/notification-prefs`).catch(() => ({ data: { log_signed: true, fall: true, urgent: true, meds: true } })),
        ]);
        setEntitlement(ent.data);
        setPrefs(prefsRes.data);
        const linkedClients = (linked.data || []).map(l => l.client);
        const seen = new Set();
        const allClients = [...cs.data, ...linkedClients].filter(c => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });
        setClients(allClients);
        const logsMap = {};
        const trendsMap = {};
        await Promise.all(allClients.map(async (c) => {
          try {
            const [logsRes, trendsRes] = await Promise.all([
              axios.get(`${API}/clients/${c.id}/care-logs`).catch(() => ({ data: [] })),
              axios.get(`${API}/clients/${c.id}/care-log-trends?days=7`).catch(() => ({ data: { logs: [] } })),
            ]);
            logsMap[c.id] = logsRes.data.filter(l => l.status === 'completed');
            trendsMap[c.id] = trendsRes.data.logs || [];
          } catch { logsMap[c.id] = []; trendsMap[c.id] = []; }
          if (logsMap[c.id].length === 0) {
            const linkedEntry = (linked.data || []).find(l => l.client.id === c.id);
            if (linkedEntry?.latest_log) logsMap[c.id] = [linkedEntry.latest_log];
          }
        }));
        setLogsByClient(logsMap);
        setTrendsByClient(trendsMap);
      } finally { setLoading(false); }
    })();
  }, []);

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await axios.put(`${API}/family/notification-prefs`, next);
      toast.success('Preferences saved');
    } catch { toast.error('Failed to save preferences'); }
  };

  if (loading) return <div className="loading-screen">Loading family dashboard…</div>;

  if (!entitlement?.entitled) {
    return (
      <div className="dashboard-container">
        <NavBar onLogout={onLogout} />
        <div className="dashboard-content page-container">
          <div className="directory-empty" data-testid="family-locked">
            <i className="fa-solid fa-lock directory-empty-icon"></i>
            <h3>Family Dashboard is locked</h3>
            <p>Subscribe to CareGuard Family to access the read-only care log dashboard for your loved ones — 7-day trends, alerts, and daily updates.</p>
            <a href="/plans" className="primary-btn" data-testid="family-locked-cta">See Plans</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" data-testid="family-dashboard">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <h1 className="page-title"><i className="fa-solid fa-people-roof" style={{ color: '#2c5364', marginRight: 10 }} /> Family Dashboard</h1>
        <p className="page-subtitle">Read-only view of your loved ones' most recent care logs.</p>

        {/* Notification preferences */}
        <div className="section-card family-prefs-card" data-testid="family-prefs">
          <h3><i className="fa-solid fa-bell" /> Email me when…</h3>
          <div className="family-prefs-row">
            {[
              { key: 'log_signed', label: 'A daily log is signed', testid: 'pref-log-signed' },
              { key: 'fall', label: 'A fall is reported', testid: 'pref-fall' },
              { key: 'urgent', label: 'Status is Urgent', testid: 'pref-urgent' },
              { key: 'meds', label: 'Medication concern documented', testid: 'pref-meds' },
            ].map(p => (
              <label key={p.key} className="family-pref-toggle">
                <input type="checkbox" checked={prefs[p.key]} onChange={e => savePrefs({ [p.key]: e.target.checked })} data-testid={p.testid} />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        </div>

        {clients.length === 0 && (
          <div className="directory-empty">
            <i className="fa-solid fa-user-plus directory-empty-icon"></i>
            <h3>No linked clients yet</h3>
            <p>Ask the primary caregiver to add you as a family member for one of their clients — Phase 4b will build magic-link invites.</p>
          </div>
        )}

        {clients.map(client => {
          const logs = logsByClient[client.id] || [];
          const today = logs[0];
          const status = today ? STATUS_COLORS[today.overall_status] : null;
          return (
            <div key={client.id} className="section-card family-client-card" data-testid={`family-client-${client.id}`}>
              <div className="family-client-header">
                <div>
                  <h2 className="family-client-name">{client.name}</h2>
                  {today && (
                    <div className="family-client-status" style={{ background: status?.bg, color: status?.color }}>
                      {status?.icon} {status?.label} · Log signed {today.log_date}
                    </div>
                  )}
                  {!today && <div className="family-client-status" style={{ background: '#f1f5f9', color: '#94a3b8' }}>⚪ No signed log yet</div>}
                </div>
              </div>
              {today && (
                <>
                  <h3 className="family-section-title">Today's Snapshot</h3>
                  <div className="family-snap-grid">
                    <Snap label="Mood" value={today.mood} icon="fa-face-smile" />
                    <Snap label="Breakfast" value={today.breakfast_amount} icon="fa-utensils" />
                    <Snap label="Lunch" value={today.lunch_amount} icon="fa-utensils" />
                    <Snap label="Dinner" value={today.dinner_amount} icon="fa-utensils" />
                    <Snap label="Appetite" value={today.appetite} icon="fa-carrot" />
                    <Snap label="Pain" value={today.pain_present ? `${today.pain_level}/10 · ${today.pain_location || ''}` : 'None'} icon="fa-hand-dots" />
                    <Snap label="Fall" value={today.fall_today ? 'REPORTED' : 'None'} icon="fa-triangle-exclamation" />
                  </div>

                  {(today.bp_systolic || today.pulse || today.temperature || today.oxygen_saturation || today.weight) && (
                    <>
                      <h3 className="family-section-title">Today's Vitals</h3>
                      <div className="family-snap-grid">
                        <Snap label="BP" value={today.bp_systolic ? `${today.bp_systolic}/${today.bp_diastolic}` : null} icon="fa-heart-pulse" />
                        <Snap label="Pulse" value={today.pulse ? `${today.pulse} bpm` : null} icon="fa-heart-pulse" />
                        <Snap label="Temp" value={today.temperature ? `${today.temperature}°F` : null} icon="fa-thermometer" />
                        <Snap label="SpO₂" value={today.oxygen_saturation ? `${today.oxygen_saturation}%` : null} icon="fa-lungs" />
                        <Snap label="Weight" value={today.weight ? `${today.weight} lb` : null} icon="fa-weight-scale" />
                        <Snap label="Glucose" value={today.blood_glucose ? `${today.blood_glucose} mg/dL` : null} icon="fa-droplet" />
                      </div>
                    </>
                  )}

                  {today.daily_summary && (
                    <>
                      <h3 className="family-section-title">Caregiver's Note</h3>
                      <p className="family-caregiver-note">{today.daily_summary}</p>
                      {today.signed_by && (
                        <p style={{ color: '#6b8899', fontSize: '0.8rem', marginTop: '-0.5rem' }}>Signed by {today.signed_by}</p>
                      )}
                    </>
                  )}

                  {today.fall_today && (
                    <div className="family-fall-alert" data-testid={`family-fall-${client.id}`}>
                      <i className="fa-solid fa-triangle-exclamation" />
                      <div>
                        <strong>Fall reported</strong> {today.fall_time && <>at {today.fall_time}</>}
                        {today.fall_narrative && <div style={{ marginTop: 6, fontSize: '0.88rem' }}>{today.fall_narrative}</div>}
                      </div>
                    </div>
                  )}
                </>
              )}
              {logs.length > 1 && (
                <p style={{ marginTop: '1rem', color: '#6b8899', fontSize: '0.85rem' }}>
                  <i className="fa-solid fa-clock-rotate-left" /> {logs.length - 1} earlier signed log(s) available.
                </p>
              )}
              {/* 7-day trend charts */}
              {(trendsByClient[client.id] || []).length > 1 && (
                <>
                  <h3 className="family-section-title">7-Day Trends</h3>
                  <div style={{ width: '100%', height: 240 }} data-testid={`trends-chart-${client.id}`}>
                    <ResponsiveContainer>
                      <LineChart data={(trendsByClient[client.id] || []).map(l => ({
                        date: l.log_date?.slice(5) || '',
                        pain: l.pain_level ?? null,
                        sleep: l.sleep_hours ?? null,
                        glucose: l.blood_glucose ?? null,
                      }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" fontSize={11} />
                        <YAxis yAxisId="left" fontSize={11} />
                        <YAxis yAxisId="right" orientation="right" fontSize={11} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                        <Line yAxisId="left" type="monotone" dataKey="pain" name="Pain (0-10)" stroke="#dc2626" strokeWidth={2} connectNulls />
                        <Line yAxisId="left" type="monotone" dataKey="sleep" name="Sleep (hrs)" stroke="#7c3aed" strokeWidth={2} connectNulls />
                        <Line yAxisId="right" type="monotone" dataKey="glucose" name="Glucose (mg/dL)" stroke="#f59e0b" strokeWidth={2} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p style={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center', marginTop: 4 }}>
                    Documented values only. CareGuard doesn't diagnose — interpretation stays with the care plan/provider.
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FamilyDashboard;
